import { Injectable } from '@angular/core';
import { BehaviorSubject, timer } from "rxjs";
import { AppService } from 'src/app/app.service';
import { AuthService } from 'src/app/auth/service/auth/auth.service';
import { UserService } from '../user/user.service';
import { SessionService } from '../session/session.service';
const createGuest = require('cross-domain-storage/guest');

@Injectable({
  providedIn: 'root'
})
export class CrossStorageService {

  private static isRefreshing = "IS_REFRESHING";
  public static SUB_DOMAIN = "SUB_DOMAIN";

  isInfoSet: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private isSubdomainSet = false;

  private authInfo = new Map([
    [AuthService.JWT_TOKEN, false],
    [AuthService.EXPIRES_ON, false],
    [AuthService.REFRESH_TOKEN_TIME, false],
  ])

  private userInfo = new Map([
    [UserService.userEmail, false],
    [UserService.productAccess, false],
    [UserService.userNo, false],
    [UserService.userName, false],
  ])

  private storageGuest: any;

  lastIdleTime: string = "-1";

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private userService: UserService,
  ) {
    this.initializeIsRefreshRequested();
    this.initCrossStorageGuest();
    this.startTimer();
  }

  
  initCrossStorageGuest(maxRetries = 20, delay = 300) {
    let authUrl = this.appService.getAuthUrl();
    if (!authUrl || !authUrl.startsWith("http")) {
      console.warn("Auth URL not ready, retrying...");
      if (maxRetries > 0) {
        setTimeout(() => this.initCrossStorageGuest(maxRetries - 1, delay), delay);
      } else {
        console.error("Auth URL never became ready. Logging out.");
        this.logout();
      }
      return;
    }
    // Build iframe src
    const iframeSrc = `${authUrl}/accessStorage`;
    // Recreate if stale guest exists
    if (this.storageGuest && this.storageGuest.origin !== authUrl) {
      console.warn("Closing stale guest with origin", this.storageGuest.origin);
      try { this.storageGuest.close(); } catch { }
      this.storageGuest = null;
    }
    // Create new guest
    if (!this.storageGuest) {
      console.log("Creating storage guest with", iframeSrc);
      this.storageGuest = createGuest(iframeSrc);
      // Attach origin for later checks
      this.storageGuest.origin = authUrl;
    }
    //  Wait until iframe actually loads
    this.waitForGuestReady(maxRetries, delay);
  }

  private waitForGuestReady(retries: number, delay: number) {
    if (!this.storageGuest) return;
    this.storageGuest.get(AuthService.JWT_TOKEN, (err, jwt) => {
      if (err) {
        // Handle "null" origin or not loaded yet
        console.warn("Guest iframe not ready:", err);
        const iframeEl = document.querySelector('iframe[src*="accessStorage"]') as HTMLIFrameElement;
        if (iframeEl) {
          console.log("Current iframe src:", iframeEl.src);
        }
        if (retries > 0) {
          setTimeout(() => this.waitForGuestReady(retries - 1, delay), delay);
        } else {
          console.error("Guest iframe never became ready. Logging out.");
          this.logout();
        }
        return;
      }
      //Success: iframe loaded with correct origin
      console.log("Guest iframe is ready at:", this.storageGuest.origin);
      this.setIsRefreshing(true);
      this.setJWT();
    });
  }



  getSubdomain(): string {
    return localStorage.getItem(CrossStorageService.SUB_DOMAIN) ?? "";
  }

 setJWT() {
  this.storageGuest.get(AuthService.JWT_TOKEN, (error, jwt) => {
    if (error) {
      console.warn('JWT token not available yet', error);
      return;
    }
    if (!jwt) this.closeGuestAndNavigateToAuth();
    this.authService.storeJwtToken(jwt);
    this.authInfo.set(AuthService.JWT_TOKEN, true);
    this.setRefreshTokenTime();
  });
}


  setRefreshTokenTime() {
    this.storageGuest.get(AuthService.REFRESH_TOKEN_TIME, (error, refreshTokenTime) => {
      this.authService.setRefreshTokenTime(refreshTokenTime);
      this.authInfo.set(AuthService.REFRESH_TOKEN_TIME, true);
      this.setExpiresOn();
    });
  }

  setExpiresOn() {
    this.storageGuest.get(AuthService.EXPIRES_ON, (error, expiresOn) => {
      expiresOn ? this.authService.storeExpiresOn(expiresOn) : this.logout();
      this.authInfo.set(AuthService.EXPIRES_ON, true);
      this.setUserEmail();
    });
  }

  setUserEmail() {
    this.storageGuest.get(UserService.userEmail, (error, email) => {
      this.userService.setUserEmail(email);
      this.userInfo.set(UserService.userEmail, true);
      this.setUserName();
    });
  }

  setUserName() {
    this.storageGuest.get(UserService.userName, (error, userName) => {
      this.userService.setUserName(userName);
      this.userInfo.set(UserService.userName, true);
      this.setUserNo();
    });
  }

  setUserNo() {
    this.storageGuest.get(UserService.userNo, (error, userName) => {
      this.userService.setUserNo(userName);
      this.userInfo.set(UserService.userNo, true);
      this.setProductAccess();
    });
  }

  setProductAccess() {
    this.storageGuest.get(UserService.productAccess, (error, productAccess) => {
      this.userService.setProductAccess(productAccess);
      this.userInfo.set(UserService.productAccess, true);
      this.setSubdomain();
    });
  }

  setSubdomain() {
    this.storageGuest.get(CrossStorageService.SUB_DOMAIN, (error, subdomain) => {
      localStorage.setItem(CrossStorageService.SUB_DOMAIN, subdomain);
      this.isSubdomainSet = true;
      this.setProductId();
    });
  }

  getAuthIdleTime() {
    this.storageGuest.get(SessionService.IDLE_SESSION_TIME, (error, authIdleTime: string) => {
      const authIdleTimeInSec: number = parseInt(authIdleTime);
      if (Number.isNaN(authIdleTimeInSec)) return;
      const umIdleTimeInSec: number = parseInt(localStorage.getItem(SessionService.IDLE_SESSION_TIME));
      localStorage.setItem(SessionService.IDLE_SESSION_TIME, Math.max(authIdleTimeInSec, umIdleTimeInSec).toString());
    });
  }

  setProductId() {
    localStorage.setItem(UserService.productId, this.appService.getProductId());
    this.isAllUserInfoSet();
  }

  resetIdleTimeInAuth(newIdleTime: string) {
    if (!this.isInfoSet.getValue() || (newIdleTime == this.lastIdleTime)) return;
    this.lastIdleTime = newIdleTime;
    this.storageGuest.set(SessionService.IDLE_SESSION_TIME, newIdleTime, function (error, data) {
    });
  }

  initializeIsRefreshRequested() {
    this.setIsRefreshing(false)
  }

  setIsRefreshing(isRefreshing: boolean) {
    localStorage.setItem(CrossStorageService.isRefreshing, isRefreshing.toString());
  }

  getIsRefreshing(): boolean {
    return JSON.parse(localStorage.getItem(CrossStorageService.isRefreshing));
  }

  navigateToAuthWebsite() {
    const productUrl = this.appService.getAuthUrl();
    const url = `${productUrl}`;
    window.open(url, "_self");
  }

  isAllUserInfoSet() {
    const isAnyInfoNotSet = [...this.authInfo.values(), ...this.userInfo.values(), this.isSubdomainSet].includes(false);
    this.isInfoSet.next(!isAnyInfoNotSet);
    this.setIsRefreshing(false);
  }

  updateToken() {
    this.setIsRefreshing(true);
    this.storageGuest.get(AuthService.JWT_TOKEN, (error, value) => {
      value ? this.authService.storeJwtToken(value) : this.navigateToAuthWebsite();
      this.storageGuest.get(AuthService.EXPIRES_ON, (error, value) => {
        value ? this.authService.storeExpiresOn(value) : this.logout();
        this.setIsRefreshing(false);
      });
    });
  }

	startTimer() {
    timer(0, 1000).subscribe((time) => {
      // * cross storage is initialized and token is set in current localStorage
      if (this.authService.isLoggedIn() && this.isInfoSet.getValue()) {
        this.updateToken();
        this.getAuthIdleTime();
      }
      // * cross storage is initialized and user pressed logout in other tab
      if (!this.authService.isLoggedIn() && this.isInfoSet.getValue()) {
        this.isInfoSet.next(false);
      }
      // * returning user, init cross storage
      if (this.authService.isLoggedIn() && !this.isInfoSet.getValue()) {
        if (!this.getIsRefreshing) this.setJWT();
      }
		});
  }

  closeGuest() {
    this.storageGuest.close();
  }

  resetInfo() {
    this.authInfo.forEach((value, key, map) => map.set(key, false));
    this.userInfo.forEach((value, key, map) => map.set(key, false));
  }

  logoutHostAndCloseGuest() {
    console.log("logout host")
    setTimeout(() => {
      this.storageGuest.remove(AuthService.JWT_TOKEN, (error, value) => {
        console.log("host token removed", { error, value })
        setTimeout(() => {
          this.closeGuestAndNavigateToAuth();
        }, 2000)
      });
    }, 0)
  }

  closeGuestAndNavigateToAuth() {
    this.authService.logout();
    this.closeGuest();
    this.navigateToAuthWebsite();
  }

  logout() {
    console.log("logout")
    this.logoutHostAndCloseGuest();
  }
}
